"use client";

import { Building2, ChevronsUpDown, Plus } from "lucide-react";
import type { Route } from "next";
import dynamic from "next/dynamic";
import Image from "next/image";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import * as React from "react";

const OrganizationFormDialog = dynamic(
  () => import("@/features/organizations/ui/organization-form-dialog"),
  { ssr: false }
);

const iconMap = {
  building2: Building2,
} as const;

function getIconComponent(iconName: string) {
  return iconMap[iconName as keyof typeof iconMap] || Building2;
}

import { Badge } from "@workspace/ui/components/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@workspace/ui/components/sidebar";
import {
  ALL_REPORT_GROUP_PERMISSIONS,
  ALL_ROUTE_PERMISSION_KEYS,
  getRoutePermission,
} from "@/lib/constants/permissions";

type Team = {
  name: string;
  logo: string;
  logoUrl?: string | null;
  planLabel: string;
  id?: string;
  slug?: string;
  permissions?: string[];
};

const RESOURCE_ID_SEGMENT_PATTERN =
  /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

const PERMISSION_FALLBACK_ROUTES: Record<string, string> = {
  "org:reports:read": "",
  "org:sales:read": "/ventas",
  "org:orders:read": "/pedidos",
  "org:preparacion:write": "/preparacion",
  "org:products:read": "/productos",
  "org:inventory:read": "/inventario",
  "org:stock_movements:read": "/movimientos-inventario",
  "org:prices:read": "/precios",
  "org:customers:read": "/clientes",
  "org:purchases:read": "/compras",
  "org:suppliers:read": "/proveedores",
  "org:contabilidad:read": "/contabilidad",
  "org:settings:write": "/configuracion",
  "org:import:write": "/importar",
  "org:checks:read": "/cheques",
  "org:treasury:read": "/tesoreria",
  "org:transfers:read": "/transferencias",
};

function getCurrentOrgSlug(params: ReturnType<typeof useParams>) {
  const orgSlug = params.orgSlug;
  if (Array.isArray(orgSlug)) {
    return orgSlug[0] ?? null;
  }
  return orgSlug ?? null;
}

function getFallbackPath(orgSlug: string, permissions: string[]) {
  for (const permission of ALL_ROUTE_PERMISSION_KEYS) {
    if (permissions.includes(permission)) {
      return `/${orgSlug}${PERMISSION_FALLBACK_ROUTES[permission] ?? ""}`;
    }
  }

  if (
    ALL_REPORT_GROUP_PERMISSIONS.some((permission) =>
      permissions.includes(permission)
    )
  ) {
    return `/${orgSlug}/reportes`;
  }

  return `/${orgSlug}`;
}

function getOrganizationSafeSuffix(pathname: string, currentOrgSlug: string) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== currentOrgSlug) {
    return "";
  }

  const suffixSegments = segments.slice(1);
  const resourceIdIndex = suffixSegments.findIndex((segment) =>
    RESOURCE_ID_SEGMENT_PATTERN.test(segment)
  );

  const safeSegments =
    resourceIdIndex === -1
      ? suffixSegments
      : suffixSegments.slice(0, resourceIdIndex);

  return safeSegments.length > 0 ? `/${safeSegments.join("/")}` : "";
}

function getTeamPath({
  currentOrgSlug,
  pathname,
  search,
  team,
}: {
  currentOrgSlug: string;
  pathname: string;
  search: string;
  team: Team;
}) {
  if (!team.slug) {
    return null;
  }

  const permissions = team.permissions ?? [];
  const suffix = getOrganizationSafeSuffix(pathname, currentOrgSlug);
  const targetPath = `/${team.slug}${suffix}`;
  const requiredPermission = getRoutePermission(targetPath);

  if (requiredPermission && !permissions.includes(requiredPermission)) {
    return getFallbackPath(team.slug, permissions);
  }

  const wasResourcePath =
    targetPath !== pathname.replace(`/${currentOrgSlug}`, `/${team.slug}`);
  const query = search && !wasResourcePath ? `?${search}` : "";
  return `${targetPath}${query}`;
}

export function TeamSwitcher({
  teams,
  loading = false,
}: {
  teams: Team[];
  loading?: boolean;
}) {
  const router = useRouter();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentOrgSlug = getCurrentOrgSlug(params);
  const currentTeam = teams.find((team) => team.slug === currentOrgSlug);
  const activeTeam = currentTeam || (teams.length > 0 ? teams[0] : null);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  if (loading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton disabled size="lg">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <div className="size-4 animate-pulse rounded bg-current" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="h-4 w-24 animate-pulse rounded bg-current" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!activeTeam || teams.length === 0) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton className="transition-all duration-200 data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground" />
            }
          >
            {activeTeam.logoUrl ? (
              <Image
                alt={activeTeam.name}
                className="size-4 rounded object-cover"
                height={20}
                src={activeTeam.logoUrl}
                width={20}
              />
            ) : (
              React.createElement(getIconComponent(activeTeam.logo), {
                className: "size-4",
              })
            )}
            <div className="flex items-center gap-2 text-left text-sm leading-tight">
              <span className="max-w-48 truncate font-medium md:max-w-none">
                {activeTeam.name}
              </span>
              <Badge
                className="hidden text-muted-foreground md:block"
                variant="outline"
              >
                {activeTeam.planLabel}
              </Badge>
            </div>
            <ChevronsUpDown className="ml-auto text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-(--anchor-width) min-w-56 rounded-lg"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                Empresas
              </DropdownMenuLabel>
              {teams.map((team) => (
                <DropdownMenuItem
                  className="gap-2 p-2"
                  key={team.id || team.name}
                  onClick={() => {
                    if (currentOrgSlug && pathname) {
                      const targetPath = getTeamPath({
                        currentOrgSlug,
                        pathname,
                        search: searchParams.toString(),
                        team,
                      });
                      if (targetPath) {
                        router.push(targetPath as Route);
                      }
                    }
                  }}
                >
                  {team.logoUrl ? (
                    <Image
                      alt={team.name}
                      className="size-4 shrink-0 rounded object-cover"
                      height={20}
                      src={team.logoUrl}
                      width={20}
                    />
                  ) : (
                    React.createElement(getIconComponent(team.logo), {
                      className: "size-4 shrink-0",
                    })
                  )}
                  {team.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 p-2"
              onClick={() => setCreateDialogOpen(true)}
            >
              <div className="flex size-6 items-center justify-center rounded-lg border bg-transparent">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">
                Agregar empresa
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <OrganizationFormDialog
        onOpenChange={setCreateDialogOpen}
        open={createDialogOpen}
      />
    </SidebarMenu>
  );
}
